import { useState } from "react";
import { toast, Toaster } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { trackEvent, getAttribution } from "@/lib/analytics";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, ...getAttribution() });
      trackEvent("contact_form_submit", { subject: form.subject || "not_provided" });
      toast.success("Message sent — we'll get back to you within 1 business day.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="page-contact">
      <Toaster richColors position="top-right" />
      <section className="pt-40 pb-16">
        <div className="wrap grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <div className="eyebrow eyebrow-red">Say hi · Prescott Valley</div>
            <h1 className="h-display text-6xl md:text-7xl uppercase mt-4">Let&apos;s talk <br/><span className="italic font-normal">real quick.</span></h1>
            <p className="mt-6 text-neutral-600 max-w-lg">
              Prefer a phone call? Text? Email? A visit to the shop? Whatever&apos;s easiest — we&apos;ll match your speed.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[var(--bg-2)] grid place-items-center border border-neutral-200"><Phone size={18} className="text-[var(--red)]"/></div>
                <div>
                  <div className="eyebrow">Call / text</div>
                  <a href="tel:+19282730784" data-testid="contact-phone" className="font-display text-2xl block mt-1">(928) 273-0784</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[var(--bg-2)] grid place-items-center border border-neutral-200"><Mail size={18} className="text-[var(--red)]"/></div>
                <div>
                  <div className="eyebrow">Email</div>
                  <a href="mailto:Quotes@928wraps.com" data-testid="contact-email" className="font-display text-2xl block mt-1">Quotes@928wraps.com</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[var(--bg-2)] grid place-items-center border border-neutral-200"><MapPin size={18} className="text-[var(--red)]"/></div>
                <div>
                  <div className="eyebrow">Studio</div>
                  <div className="font-display text-2xl mt-1">8920 E Valley Rd</div>
                  <div className="text-neutral-600">Prescott Valley, AZ 86314</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[var(--bg-2)] grid place-items-center border border-neutral-200"><Clock size={18} className="text-[var(--red)]"/></div>
                <div>
                  <div className="eyebrow">Hours</div>
                  <div className="text-neutral-800 mt-1">Mon – Fri · 9:00 AM – 5:00 PM</div>
                  <div className="text-neutral-500 text-sm">By appointment on Saturdays</div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-6">
            <form onSubmit={submit} data-testid="contact-form" className="bg-[var(--bg-2)] border border-neutral-200 rounded-3xl p-8 md:p-10">
              <div className="eyebrow eyebrow-red">Send a message</div>
              <h2 className="h-display text-3xl md:text-4xl uppercase mt-3">Got a question?<br/><span className="italic font-normal text-neutral-500">Ask away.</span></h2>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required data-testid="contact-name" placeholder="Full name" className="input-928" value={form.name} onChange={set("name")} />
                <input required type="email" data-testid="contact-email-input" placeholder="Email" className="input-928" value={form.email} onChange={set("email")} />
                <input data-testid="contact-phone-input" placeholder="Phone (optional)" className="input-928" value={form.phone} onChange={set("phone")} />
                <input data-testid="contact-subject" placeholder="Subject" className="input-928" value={form.subject} onChange={set("subject")} />
              </div>
              <textarea required data-testid="contact-message" placeholder="Tell us about your project…" className="input-928 mt-4" value={form.message} onChange={set("message")} />
              <button disabled={loading} type="submit" data-testid="contact-submit" className="btn-red mt-6">
                {loading ? "Sending…" : "Send message"} <ArrowRight size={16}/>
              </button>
              <p className="mt-4 text-xs text-neutral-500">Prefer a real mockup? <Link to="/mockup" className="underline decoration-[var(--red)]">Request one here →</Link></p>
            </form>
          </div>
        </div>
      </section>

      <section className="pb-32">
        <div className="wrap">
          <div className="rounded-3xl overflow-hidden border border-neutral-200">
            <iframe
              title="928 Wraps location"
              src="https://www.google.com/maps?q=8920+E+Valley+Rd,+Prescott+Valley,+AZ+86314&output=embed"
              width="100%"
              height="420"
              style={{ border: 0 }}
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
